import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import { useMapVisibility } from '../useMapVisibility';
import { useAreasData } from '../../data/useAreasData';
import { useMapSelection } from '../useMapSelection';
import { useLegendConfig } from '../useLegendConfig';
import { useMetaData } from '../../data/useMetaData';
import { useAreaAggregationData } from '../../data/useAreaAggregationData';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapColorPalette } from '../useMapColorPalette';
import { useCustomAreaCreation } from '../useCustomAreaCreation';
import { useCustomAreaEdition } from '../useCustomAreaEdition';
import type { Area } from '../../../types/domain';
import { COLORS } from '../../../constants/color';
import { alpha, toRgb, type RgbColor } from '../../../utils/color';
import {
  bordersToRanges,
  calcBins,
  calcPercentile,
} from '../../../utils/statistics';
import {
  formatCountPerArea,
  formatDistance,
  formatDuration,
} from '../../../utils/format';
import type { Legend } from '../../../types/common';
import { useDataFilter } from '../useDataFilter';

type UseAreaPrefLayer = {
  areaPrefLayer: Layer;
  areaPrefLegends: Legend[];
};

type CustomFeature = Feature<Polygon | MultiPolygon> & {
  key_code: string;
  value?: number;
  tooltip: string;
};

const isCustomFeature = (
  feature: Feature<Geometry>
): feature is CustomFeature => {
  return (
    'key_code' in feature &&
    typeof feature.key_code === 'string' &&
    ('value' in feature
      ? typeof feature.value === 'number' ||
        typeof feature.value === 'undefined'
      : true) &&
    'tooltip' in feature &&
    typeof feature.tooltip === 'string'
  );
};

const useAreaPrefLayer = (): UseAreaPrefLayer => {
  const { t } = useTranslation();
  const { filteredAreaKeyCodes } = useDataFilter();
  const { areaPrefectures } = useAreasData();
  const { aggregations } = useAreaAggregationData();
  const { selectedAreaKeyCodes } = useMapSelection();
  const { isAggregationVisible } = useMapVisibility();
  const { aggregationConfig } = useAggregationConfig();
  const { scaleType, scaleSize, customRange } = useLegendConfig();
  const { currentDataMeta } = useMetaData();
  const { editingCustomArea } = useCustomAreaEdition();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();

  const isAggregating =
    aggregationConfig?.type === 'area_pref' && aggregations.size > 0;
  const viewMagnification = 1 / Number(currentDataMeta?.reduction_factor ?? 1);

  const data = useMemo((): CustomFeature[] => {
    const formatValue = (value: number): string => {
      switch (aggregationConfig?.target) {
        case 'traveled_time':
        case 'activity_time':
          return formatDuration(value);
        case 'traveled_distance':
          return formatDistance(value);
        default:
          return formatCountPerArea(value, t('unit.agent'));
      }
    };

    return Array.from(areaPrefectures.values()).map(
      (area: Area): CustomFeature => {
        const aggregation = isAggregating
          ? aggregations.get(`${area.key_code}`)
          : undefined;
        const value =
          typeof aggregation === 'number'
            ? aggregation * viewMagnification
            : undefined;
        return {
          type: 'Feature',
          geometry: area.geometry,
          properties: {},
          key_code: area.key_code,
          value: value,
          tooltip:
            value !== undefined
              ? `${area.s_name || '-'}: ${formatValue(value)}`
              : area.s_name || '-',
        };
      }
    );
  }, [
    areaPrefectures,
    aggregations,
    aggregationConfig?.target,
    isAggregating,
    viewMagnification,
    t,
  ]);

  const legends = useMemo((): Legend[] => {
    if (!isAggregating) {
      return [];
    }

    const areaValues = Array.from(aggregations.values()).map(
      (value: number): number => {
        return value * viewMagnification;
      }
    );
    const ranges =
      scaleType === 'custom'
        ? customRange
        : scaleType === 'quantize'
          ? bordersToRanges(calcBins(areaValues, scaleSize, 0.01))
          : bordersToRanges(calcPercentile(areaValues, scaleSize));

    return getRgbColors(ranges.length).map(
      (color: RgbColor, index: number): Legend => {
        return {
          color: alpha(color, mapColorOpacity),
          range: ranges[index],
        };
      }
    );
  }, [
    aggregations,
    scaleType,
    scaleSize,
    customRange,
    getRgbColors,
    mapColorOpacity,
    isAggregating,
    viewMagnification,
  ]);

  const areaPrefLayer = new GeoJsonLayer({
    id: 'area-pref-layer',
    data: data,
    getFillColor: (feature: Feature<Geometry>): RgbColor => {
      // 実際には不要であるが、型エラーを避けるために新しい変数に代入してから使用する
      const customFeature = feature;
      if (!isCustomFeature(customFeature)) {
        return toRgb(COLORS.transparent);
      }

      // 集計結果がある地域については、値の大小に応じた色分けを行う
      if (customFeature.value !== undefined) {
        return (
          legends.find((legend: Legend): boolean => {
            return (
              customFeature.value !== undefined &&
              legend.range[0] <= customFeature.value &&
              customFeature.value <= legend.range[1]
            );
          })?.color ?? toRgb(COLORS.transparent)
        );
      }
      // クリックで選択された地域は強調表示する
      if (selectedAreaKeyCodes.includes(customFeature.key_code)) {
        return toRgb(alpha('#848C12', 0.6));
      }
      // フィルターの対象になっている地域も強調表示する
      if (filteredAreaKeyCodes.includes(customFeature.key_code)) {
        return toRgb(alpha(COLORS.gray, 0.6));
      }
      // それ以外の地域はデータの対象範囲内かどうかに関わらず一律で透明にする
      return toRgb(COLORS.transparent);
    },
    getLineColor: toRgb(alpha('#8C8C8C', 0.3)),
    getLineWidth: 10,
    lineWidthUnits: 'meters',
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 3,
    lineJointRounded: true,
    autoHighlight: !creatingCustomArea && !editingCustomArea,
    highlightColor: toRgb(alpha('#848C12', 0.6)),
    pickable: !creatingCustomArea && !editingCustomArea,
    visible: isAggregationVisible && aggregationConfig?.type === 'area_pref',
    updateTriggers: {
      getFillColor: [legends, selectedAreaKeyCodes, filteredAreaKeyCodes],
    },
  });

  return { areaPrefLayer: areaPrefLayer, areaPrefLegends: legends };
};

export { useAreaPrefLayer };
